/* PD Navigation :
   - ordinateur : méga-menus au survol, au clic et au clavier (Entrée, Espace, flèche bas, Échap) ;
   - mobile (et ordinateur avec la composition « Menu ») : un seul panneau qui défile verticalement,
     collections regroupées par menu parent (focus piégé, Échap, voile, glisser vers la gauche). */
(function () {
  const desktopQuery = window.matchMedia('(min-width: 1025px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ---------- Méga-menus (ordinateur) ---------- */
  function initDesktop(header) {
    const items = [...header.querySelectorAll('.pd-nav__item.has-mega')];
    if (!items.length) return;
    let closeTimer = null;

    const isOpen = (item) => item.classList.contains('is-open');
    const linkOf = (item) => item.querySelector('[data-pd-nav-parent]');
    const panelOf = (item) => item.querySelector('[data-pd-nav-mega]');

    const close = (item, { focusLink = false } = {}) => {
      if (!isOpen(item)) return;
      item.classList.remove('is-open');
      linkOf(item).setAttribute('aria-expanded', 'false');
      const panel = panelOf(item);
      setTimeout(() => {
        if (!isOpen(item)) panel.hidden = true;
      }, reducedMotion ? 0 : 200);
      if (!items.some(isOpen)) header.classList.remove('pd-nav-open');
      if (focusLink) linkOf(item).focus();
    };

    const closeAll = (except) => items.forEach((item) => item !== except && close(item));

    const open = (item) => {
      clearTimeout(closeTimer);
      closeAll(item);
      if (isOpen(item)) return;
      const panel = panelOf(item);
      panel.hidden = false;
      void panel.offsetWidth; // permet le fondu après l'affichage
      item.classList.add('is-open');
      linkOf(item).setAttribute('aria-expanded', 'true');
      header.classList.add('pd-nav-open');
    };

    items.forEach((item) => {
      const link = linkOf(item);
      item.addEventListener('mouseenter', () => desktopQuery.matches && open(item));
      item.addEventListener('mouseleave', () => {
        if (!desktopQuery.matches) return;
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => close(item), 250);
      });

      // Clavier : Entrée / Espace / flèche bas ouvrent d'abord le panneau ; un 2e Entrée suit le lien
      link.addEventListener('keydown', (event) => {
        if (!desktopQuery.matches) return;
        if ((event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') && !isOpen(item)) {
          event.preventDefault();
          open(item);
          const first = panelOf(item).querySelector('a');
          if (first && event.key === 'ArrowDown') first.focus();
        }
      });

      // Fermeture quand le focus quitte l'élément
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) close(item);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const opened = items.find(isOpen);
      if (opened) close(opened, { focusLink: opened.contains(document.activeElement) });
    });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.pd-nav__item.has-mega')) closeAll();
    });
    desktopQuery.addEventListener('change', () => closeAll());
  }

  /* ---------- Panneau mobile ---------- */
  function initDrawer(header) {
    const burger = header.querySelector('[data-pd-nav-open]');
    const drawer = header.querySelector('[data-pd-nav-drawer]') || document.getElementById(burger && burger.getAttribute('aria-controls'));
    if (!burger || !drawer) return;
    // Hors du header : évite tout effet de position (header collant, transformations)
    if (drawer.parentElement !== document.body) {
      // Éditeur de thème : retire l'ancien panneau avant d'installer le nouveau
      document.querySelectorAll(`[id="${drawer.id}"]`).forEach((el) => el !== drawer && el.remove());
      document.body.appendChild(drawer);
    }
    const panel = drawer.querySelector('[data-pd-nav-panel]');
    let closeTimer = null;

    // Éléments atteignables au clavier dans le panneau
    const focusables = () => [...panel.querySelectorAll(FOCUSABLE)];

    const openDrawer = () => {
      clearTimeout(closeTimer);
      drawer.hidden = false;
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      burger.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      const close = drawer.querySelector('.pd-nav-drawer__close');
      if (close) close.focus();
    };

    const closeDrawer = () => {
      if (!drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      burger.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      closeTimer = setTimeout(() => {
        drawer.hidden = true;
      }, reducedMotion ? 0 : 300);
      burger.focus();
    };

    burger.addEventListener('click', openDrawer);
    drawer.addEventListener('click', (event) => {
      if (event.target.closest('[data-pd-nav-close]')) closeDrawer();
    });

    // Échap et focus piégé
    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Glisser vers la gauche pour fermer (on suit le doigt : le panneau défile aussi verticalement)
    let startX = null;
    let startY = null;
    let lastX = null;
    let lastY = null;
    panel.addEventListener('touchstart', (e) => {
      startX = lastX = e.touches[0].clientX;
      startY = lastY = e.touches[0].clientY;
    }, { passive: true });
    panel.addEventListener('touchmove', (e) => {
      lastX = e.touches[0].clientX;
      lastY = e.touches[0].clientY;
    }, { passive: true });
    const endSwipe = () => {
      if (startX === null) return;
      const dx = lastX - startX;
      const dy = Math.abs(lastY - startY);
      if (dx < -70 && Math.abs(dx) > dy * 1.5) closeDrawer();
      startX = null;
    };
    panel.addEventListener('touchend', endSwipe);
    panel.addEventListener('touchcancel', endSwipe);

    // Composition classique : pas de panneau sur ordinateur ; composition « Menu » : le panneau sert aussi sur ordinateur
    desktopQuery.addEventListener('change', () => desktopQuery.matches && !header.classList.contains('pd-header--menu') && closeDrawer());
  }

  function init(scope) {
    scope.querySelectorAll('header.header').forEach((header) => {
      if (header.dataset.pdNavReady) return;
      header.dataset.pdNavReady = 'true';
      initDesktop(header);
      initDrawer(header);
    });
  }

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
